import { omit, pick } from "lodash";
import {
    AndFilter,
    Experience,
    InProductScheduleFilter,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleInitialSequences,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleLinkTag,
    Schedule,
    Seller,
} from "types";
import { api } from "../../../api/api";
import { getScheduleSummary } from "../../../helpers/scheduleSummaryHelper";
import { PreferenceLabels } from "../../../models/DuplicateExperience";
import { isInProductScheduleFilter } from "../../../models/PurchaseRule";
import { omitId, saveData } from "./DuplicateHelper";

const allowedSchedulesPurchaseRulesMetaFields = ["schedules", "variationWeight"];

export type SchedulesMap = Array<{ old: string; new: string }>;

interface SaveSchedulesProps {
    schedules: Schedule[];
    schedulePurchaseRuleLinks: PurchaseRuleLink[];
    experience: Experience;
    seller: Seller;
    path: string;
}

export const saveSchedules = async ({
    schedules,
    schedulePurchaseRuleLinks,
    experience,
    seller,
    path,
}: SaveSchedulesProps) => {
    const schedulesMap: SchedulesMap = [];

    await Promise.all(
        schedules.map(async (schedule: Schedule) => {
            const payload = omit(schedule, ["id", "createdAt", "createdBy", "updatedAt", "updatedBy", "priceDelta"]);
            const item = `${PreferenceLabels.SCHEDULES}, name: ${schedule.name || getScheduleSummary(schedule)}`;

            const response = await saveData<Schedule>({ payload, path, item });

            if (schedule.id && response?.data?.id) {
                schedulesMap.push({ old: schedule.id, new: response.data.id });
            }
        }),
    );

    if (schedulePurchaseRuleLinks.length === 0) {
        return schedulesMap;
    }

    await saveSchedulesRulesAndLinks({
        schedulesMap,
        schedulePurchaseRuleLinks,
        experience,
        seller,
    });

    return schedulesMap;
};

interface saveSchedulesRulesAndLinksProps {
    schedulesMap: Array<{ old: string; new: string }>;
    schedulePurchaseRuleLinks: PurchaseRuleLink[];
    experience: Experience;
    seller: Seller;
}

export const saveSchedulesRulesAndLinks = async ({
    schedulesMap,
    schedulePurchaseRuleLinks,
    experience,
    seller,
}: saveSchedulesRulesAndLinksProps) => {
    const newRules: Array<{ purchaseRule: PurchaseRule; oldSchedules: string[] }> = [];

    let purchaseRulesToLink: Array<{ purchaseRule: PurchaseRule }> = [];

    for (const schedule of schedulesMap) {
        const schedulePurchaseRules = schedulePurchaseRuleLinks[0].purchaseRules.filter((purchaseRule) => {
            const rule = purchaseRule.purchaseRule as PurchaseRule;

            return rule.meta?.schedules?.includes(schedule.old);
        });

        for (const purchaseRule of schedulePurchaseRules) {
            const existingRule = newRules.find((rule) => {
                const purchaseRuleToCopy = purchaseRule.purchaseRule as PurchaseRule;
                const includesOldSchedule = rule.oldSchedules.includes(schedule.old);
                const isSameName = rule.purchaseRule.name === purchaseRuleToCopy.name;

                return includesOldSchedule && isSameName;
            });

            if (existingRule) {
                const ruleToUpdate = existingRule.purchaseRule as Required<PurchaseRule>;
                ruleToUpdate.meta.schedules = [...new Set([...(ruleToUpdate.meta.schedules ?? []), schedule.new])];
                ruleToUpdate.meta.product = experience.id;

                if (ruleToUpdate.filter?.filters) {
                    ruleToUpdate.filter.filters = (ruleToUpdate.filter as AndFilter).filters.map((filter) => {
                        if (isInProductScheduleFilter(filter)) {
                            return {
                                ...omitId<InProductScheduleFilter>(filter),
                                schedules: {
                                    ...filter.schedules,
                                    items: [...new Set([...filter.schedules.items, schedule.new])],
                                },
                            };
                        }

                        return {
                            ...omitId<InProductScheduleFilter>(filter),
                        };
                    });
                }

                const purchaseRulePayload = {
                    ...ruleToUpdate,
                    filter: ruleToUpdate.filter,
                };

                const { data } = await api.put<PurchaseRule>(
                    `/purchaseRules/${existingRule.purchaseRule.id}`,
                    purchaseRulePayload,
                );

                newRules.map((rule) =>
                    rule.purchaseRule.id === data.id
                        ? {
                              ...rule,
                              purchaseRule: data,
                          }
                        : rule,
                );

                purchaseRulesToLink = purchaseRulesToLink.map((rule) => {
                    if (rule.purchaseRule.id === data.id) {
                        return {
                            ...rule,
                            purchaseRule: data,
                        };
                    }

                    return rule;
                });
            } else {
                const purchaseRuleToCopy = purchaseRule.purchaseRule as PurchaseRule;

                const ruleToCreate = {
                    ...omit(purchaseRuleToCopy, ["id", "permanentId", "createdAt", "updatedAt", "createdBy"]),
                    tags: purchaseRuleToCopy.tags?.map((tag) => pick(tag, ["id"])),
                    meta: pick(purchaseRuleToCopy.meta, allowedSchedulesPurchaseRulesMetaFields),
                };

                const oldSchedules =
                    ruleToCreate.meta && Array.isArray(ruleToCreate.meta?.schedules) ? ruleToCreate.meta.schedules : [];
                const filters = (ruleToCreate.filter as AndFilter).filters.map((filter) => {
                    if (isInProductScheduleFilter(filter)) {
                        return {
                            ...omitId<InProductScheduleFilter>(filter),
                            schedules: {
                                ...filter.schedules,
                                items: [schedule.new],
                            },
                            product: { id: experience.id },
                        };
                    }

                    return {
                        ...omitId<InProductScheduleFilter>(filter),
                    };
                });

                const purchaseRulePayload = {
                    ...ruleToCreate,
                    actions: ruleToCreate.actions?.map((action) => omitId<PurchaseRuleAction>(action)),
                    meta: {
                        ...ruleToCreate.meta,
                        product: experience.id,
                        schedules: [schedule.new],
                    },
                    filter: {
                        ...ruleToCreate.filter,
                        filters,
                    },
                };

                const { data } = await api.post<PurchaseRule>(`/purchaseRules`, purchaseRulePayload);

                purchaseRulesToLink.push({ purchaseRule: data });
                newRules.push({ purchaseRule: data, oldSchedules });
            }
        }
    }

    const purchaseRuleLinksPayload = {
        entities: [{ id: experience.id, object: PurchaseRuleLinkEntityObject.EXPERIENCE }],
        purchaseRules: purchaseRulesToLink.map((rule, index) => ({
            ...rule,
            sequence: PurchaseRuleInitialSequences.SCHEDULE_SEQUENCE + index,
        })),
        tags: [{ id: PurchaseRuleLinkTag.SCHEDULE }],
        seller: { id: seller.id },
    };

    await saveData({
        payload: purchaseRuleLinksPayload,
        path: `/purchaseRuleLinks`,
        item: PreferenceLabels.SCHEDULE_PURCHASE_RULES,
    });
};
